import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import MetaTags from "react-meta-tags";
import axios from "axios";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
} from "react-bootstrap-table2-paginator";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Label,
  Input,
  Button,
} from "reactstrap";
import Select from "react-select";
import Breadcrumbs from "components/Common/Breadcrumb";
import { getDeletedPayments } from "store/Payment/actions";

class DeletedPayments extends Component {
  // Formats any date value as DD-MM-YY, optionally with HH:mm appended.
  formatDateDDMMYY = (value, withTime = false) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d)) return "-";

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const day = String(d.getDate()).padStart(2, "0");
    const month = months[d.getMonth()];
    const year = String(d.getFullYear()).slice(-2);
    const datePart = `${day}-${month}-${year}`;

    if (!withTime) return datePart;

    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${datePart} ${hours}:${minutes}`;
  };

  constructor(props) {
    super(props);
    this.state = {
      // Top filter bar (drives the backend fetch)
      nameFilter: "",
      idFilter: "",
      dateFromFilter: "",
      dateToFilter: "",

      // Header column filters (client-side, like Participant Payments)
      colIdFilter: "",
      colParticipantFilter: "",
      colSchemeFilter: "",
      colPayableFilter: "",
      colFinalPayableFilter: "",
      colStatusFilter: "",
      colModeFilter: "",
      colPaydateFilter: "",
      colReasonFilter: "",
      colDeletedByFilter: "",
      colDeletedAtFilter: "",
      colRequestedByFilter: "",

      // Tracks which row is currently being restored, to disable its button
      // and avoid double-clicks while the request is in flight.
      restoringId: null,

      columns: [
        {
          dataField: "id",
          text: "Payment ID",
          sort: true,
          headerStyle: { backgroundColor: "#87ceeb", textAlign: "center" },
          style: { textAlign: "center" },
          formatter: cell => <>P-{cell}</>,
          headerFormatter: column => (
            <div style={{ textAlign: "center" }}>
              <div>{column.text}</div>
              <div style={{ marginTop: "5px" }}>
                <input
                  type="text"
                  value={this.state.colIdFilter}
                  onChange={e =>
                    this.handleColumnFilterChange("colIdFilter", e)
                  }
                  className="form-control"
                  style={{ textAlign: "center", width: "80px", margin: "auto" }}
                />
              </div>
            </div>
          ),
        },
        {
          dataField: "participant_name",
          text: "Lab / Participant",
          sort: true,
          headerStyle: { backgroundColor: "#87ceeb", textAlign: "left" },
          style: { textAlign: "left" },
          headerFormatter: column => (
            <div style={{ textAlign: "left" }}>
              <div>{column.text}</div>
              <div style={{ marginTop: "5px" }}>
                <input
                  type="text"
                  value={this.state.colParticipantFilter}
                  onChange={e =>
                    this.handleColumnFilterChange("colParticipantFilter", e)
                  }
                  className="form-control"
                  style={{ textAlign: "left", width: "140px" }}
                />
              </div>
            </div>
          ),
        },
        {
          dataField: "schemes",
          text: "Scheme / Cycle",
          headerStyle: { backgroundColor: "#87ceeb", textAlign: "center" },
          style: { textAlign: "left" },
          formatter: cell =>
            Array.isArray(cell) && cell.length > 0 ? (
              <ul className="mb-0 ps-3">
                {cell.map((s, i) => (
                  <li key={i}>
                    {s.scheme_name} (Cycle {s.cycle_no}) — Rs.{" "}
                    {Number(s.price || 0).toLocaleString()}
                  </li>
                ))}
              </ul>
            ) : (
              "-"
            ),
          headerFormatter: column => (
            <div style={{ textAlign: "center" }}>
              <div>{column.text}</div>
              <div style={{ marginTop: "5px" }}>
                <input
                  type="text"
                  value={this.state.colSchemeFilter}
                  onChange={e =>
                    this.handleColumnFilterChange("colSchemeFilter", e)
                  }
                  className="form-control"
                  style={{
                    textAlign: "center",
                    width: "140px",
                    margin: "auto",
                  }}
                />
              </div>
            </div>
          ),
        },
        {
          dataField: "priceBeforeDiscount",
          text: "Payable",
          sort: true,
          headerStyle: { backgroundColor: "#fff9c4", textAlign: "center" },
          style: { backgroundColor: "#fff9c4", textAlign: "right" },
          formatter: cell => (cell ? Number(cell).toLocaleString() : "-"),
          headerFormatter: column => (
            <div style={{ textAlign: "center" }}>
              <div>{column.text}</div>
              <div style={{ marginTop: "5px" }}>
                <input
                  type="text"
                  value={this.state.colPayableFilter}
                  onChange={e =>
                    this.handleColumnFilterChange("colPayableFilter", e)
                  }
                  className="form-control"
                  style={{ textAlign: "center", width: "70px", margin: "auto" }}
                />
              </div>
            </div>
          ),
        },
        {
          dataField: "price",
          text: "Final Payable",
          sort: true,
          headerStyle: { backgroundColor: "#fff9c4", textAlign: "center" },
          style: { backgroundColor: "#fff9c4", textAlign: "right" },
          formatter: cell => (cell ? Number(cell).toLocaleString() : "-"),
          headerFormatter: column => (
            <div style={{ textAlign: "center" }}>
              <div>{column.text}</div>
              <div style={{ marginTop: "5px" }}>
                <input
                  type="text"
                  value={this.state.colFinalPayableFilter}
                  onChange={e =>
                    this.handleColumnFilterChange("colFinalPayableFilter", e)
                  }
                  className="form-control"
                  style={{ textAlign: "center", width: "70px", margin: "auto" }}
                />
              </div>
            </div>
          ),
        },
        {
          dataField: "payment_status",
          text: "Payment Status",
          sort: true,
          headerStyle: { backgroundColor: "#d0e2ff", textAlign: "center" },
          style: { backgroundColor: "#d0e2ff", textAlign: "center" },
          headerFormatter: column => (
            <div style={{ textAlign: "center" }}>
              <div>{column.text}</div>
              <div style={{ marginTop: "5px" }}>
                <input
                  type="text"
                  value={this.state.colStatusFilter}
                  onChange={e =>
                    this.handleColumnFilterChange("colStatusFilter", e)
                  }
                  className="form-control"
                  style={{ textAlign: "center", width: "90px", margin: "auto" }}
                />
              </div>
            </div>
          ),
        },
        {
          dataField: "paymentmethod",
          text: "Payment Mode",
          headerStyle: { backgroundColor: "#d0e2ff", textAlign: "center" },
          style: { backgroundColor: "#d0e2ff", textAlign: "center" },
          headerFormatter: column => (
            <div style={{ textAlign: "center" }}>
              <div>{column.text}</div>
              <div style={{ marginTop: "5px" }}>
                <input
                  type="text"
                  value={this.state.colModeFilter}
                  onChange={e =>
                    this.handleColumnFilterChange("colModeFilter", e)
                  }
                  className="form-control"
                  style={{ textAlign: "center", width: "90px", margin: "auto" }}
                />
              </div>
            </div>
          ),
        },
        {
          dataField: "paydate",
          text: "Pay Date",
          sort: true,
          headerStyle: { backgroundColor: "#87ceeb", textAlign: "center" },
          style: { textAlign: "center" },
          formatter: cell => this.formatDateDDMMYY(cell),
          headerFormatter: column => (
            <div style={{ textAlign: "center" }}>
              <div>{column.text}</div>
              <div style={{ marginTop: "5px" }}>
                <input
                  type="date"
                  value={this.state.colPaydateFilter}
                  onChange={e =>
                    this.handleColumnFilterChange("colPaydateFilter", e)
                  }
                  className="form-control"
                  style={{
                    textAlign: "center",
                    width: "130px",
                    margin: "auto",
                  }}
                />
              </div>
            </div>
          ),
        },
        {
          dataField: "delete_comment",
          text: "Deletion Reason",
          headerStyle: { backgroundColor: "#87ceeb", textAlign: "center" },
          style: { textAlign: "left" },
          formatter: cell =>
            cell ? (
              <span style={{ color: "#dc3545" }}>{cell}</span>
            ) : (
              <span style={{ color: "#6c757d" }}>-</span>
            ),
          headerFormatter: column => (
            <div style={{ textAlign: "center" }}>
              <div>{column.text}</div>
              <div style={{ marginTop: "5px" }}>
                <input
                  type="text"
                  value={this.state.colReasonFilter}
                  onChange={e =>
                    this.handleColumnFilterChange("colReasonFilter", e)
                  }
                  className="form-control"
                  style={{
                    textAlign: "center",
                    width: "140px",
                    margin: "auto",
                  }}
                />
              </div>
            </div>
          ),
        },
        {
          dataField: "requested_by_name",
          text: "Requested By",
          headerStyle: { backgroundColor: "#87ceeb", textAlign: "center" },
          style: { textAlign: "center" },
          formatter: cell => cell || "-",
          headerFormatter: column => (
            <div style={{ textAlign: "center" }}>
              <div>{column.text}</div>
              <div style={{ marginTop: "5px" }}>
                <input
                  type="text"
                  value={this.state.colRequestedByFilter}
                  onChange={e =>
                    this.handleColumnFilterChange("colRequestedByFilter", e)
                  }
                  className="form-control"
                  style={{
                    textAlign: "center",
                    width: "120px",
                    margin: "auto",
                  }}
                />
              </div>
            </div>
          ),
        },
        {
          dataField: "deleted_by_name",
          text: "Deleted By",
          headerStyle: { backgroundColor: "#87ceeb", textAlign: "center" },
          style: { textAlign: "center" },
          formatter: cell => cell || "-",
          headerFormatter: column => (
            <div style={{ textAlign: "center" }}>
              <div>{column.text}</div>
              <div style={{ marginTop: "5px" }}>
                <input
                  type="text"
                  value={this.state.colDeletedByFilter}
                  onChange={e =>
                    this.handleColumnFilterChange("colDeletedByFilter", e)
                  }
                  className="form-control"
                  style={{
                    textAlign: "center",
                    width: "100px",
                    margin: "auto",
                  }}
                />
              </div>
            </div>
          ),
        },
        {
          dataField: "deleted_at",
          text: "Deleted At",
          sort: true,
          headerStyle: { backgroundColor: "#87ceeb", textAlign: "center" },
          style: { textAlign: "center" },
          formatter: cell => this.formatDateDDMMYY(cell, true),
          headerFormatter: column => (
            <div style={{ textAlign: "center" }}>
              <div>{column.text}</div>
              <div style={{ marginTop: "5px" }}>
                <input
                  type="date"
                  value={this.state.colDeletedAtFilter}
                  onChange={e =>
                    this.handleColumnFilterChange("colDeletedAtFilter", e)
                  }
                  className="form-control"
                  style={{
                    textAlign: "center",
                    width: "130px",
                    margin: "auto",
                  }}
                />
              </div>
            </div>
          ),
        },
        {
          dataField: "action",
          text: "Action",
          isDummyField: true,
          headerStyle: { backgroundColor: "#87ceeb", textAlign: "center" },
          style: { textAlign: "center" },
          formatter: (cell, row) => (
            <Button
              color="success"
              size="sm"
              disabled={this.state.restoringId === row.id}
              onClick={() => this.handleRestore(row)}
            >
              {this.state.restoringId === row.id ? "Restoring..." : "Restore"}
            </Button>
          ),
        },
      ],
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  fetchData = () => {
    const { idFilter, dateFromFilter, dateToFilter } = this.state;
    this.props.onGetDeletedPayments({
      ...(idFilter ? { id: idFilter } : {}),
      ...(dateFromFilter ? { date_from: dateFromFilter } : {}),
      ...(dateToFilter ? { date_to: dateToFilter } : {}),
    });
  };

  // Text/date top-bar fields (Payment ID, Deleted From, Deleted To) hit the
  // backend, so re-fetch automatically as soon as the value changes —
  // no "Apply Filters" button needed.
  handleTopFilterChange = (filterName, value) => {
    this.setState({ [filterName]: value }, this.fetchData);
  };

  // Generic handler for the per-column header inputs, mirroring
  // handleFilterChange in Participant Payments
  handleColumnFilterChange = (filterName, e) => {
    this.setState({ [filterName]: e.target.value });
  };

  // Restores a soft-deleted payment: confirms, calls the restore action,
  // then re-fetches the deleted-payments list so the row disappears here.
  // The payment will show up again on the active Payments page automatically,
  // since ActivePaymentManager only excludes rows while is_deleted is true.
  handleRestore = row => {
    const confirmed = window.confirm(
      `Restore payment P-${row.id} for ${row.participant_name}? It will become active again and reappear in the Payments list.`
    );
    if (!confirmed) return;

    this.setState({ restoringId: row.id });

    fetch(
      `https://externalqcapi.com/api/registration-admin/restore-payment/${row.id}/`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          // add your auth header here if other calls in the app send one, e.g.:
          // Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    )
      .then(response => {
        if (!response.ok) {
          throw new Error(`Restore failed with status ${response.status}`);
        }
        return response.json();
      })
      .then(() => {
        this.setState({ restoringId: null });
        this.fetchData();
      })
      .catch(() => {
        this.setState({ restoringId: null });
        window.alert("Failed to restore this payment. Please try again.");
      });
  };

  // Builds unique dropdown options (e.g. Lab / Participant names) straight
  // from whatever data is currently loaded — same pattern as
  // Participant Payments' getUniqueOptions.
  getUniqueOptions = fieldName => {
    const { DeletedPayments } = this.props;
    const options = new Set();

    (DeletedPayments || []).forEach(item => {
      const value = item[fieldName];
      if (value) options.add(value);
    });

    return Array.from(options);
  };

  // Client-side filtering across every header-level column filter,
  // plus the top "Lab / Participant Name" bar filter
  filterData = () => {
    const { DeletedPayments } = this.props;
    const {
      nameFilter,
      colIdFilter,
      colParticipantFilter,
      colSchemeFilter,
      colPayableFilter,
      colFinalPayableFilter,
      colStatusFilter,
      colModeFilter,
      colPaydateFilter,
      colReasonFilter,
      colDeletedByFilter,
      colDeletedAtFilter,
      colRequestedByFilter,
    } = this.state;

    return (DeletedPayments || []).filter(row => {
      if (
        nameFilter &&
        !row.participant_name?.toLowerCase().includes(nameFilter.toLowerCase())
      )
        return false;

      if (colIdFilter && !String(row.id || "").includes(colIdFilter))
        return false;

      if (
        colParticipantFilter &&
        !row.participant_name
          ?.toLowerCase()
          .includes(colParticipantFilter.toLowerCase())
      )
        return false;

      if (colSchemeFilter) {
        const schemeText = Array.isArray(row.schemes)
          ? row.schemes
              .map(s => `${s.scheme_name} ${s.cycle_no}`)
              .join(" ")
              .toLowerCase()
          : "";
        if (!schemeText.includes(colSchemeFilter.toLowerCase())) return false;
      }

      if (
        colPayableFilter &&
        !String(row.priceBeforeDiscount || "").includes(colPayableFilter)
      )
        return false;

      if (
        colFinalPayableFilter &&
        !String(row.price || "").includes(colFinalPayableFilter)
      )
        return false;

      if (
        colStatusFilter &&
        !row.payment_status
          ?.toLowerCase()
          .includes(colStatusFilter.toLowerCase())
      )
        return false;

      if (
        colModeFilter &&
        !row.paymentmethod?.toLowerCase().includes(colModeFilter.toLowerCase())
      )
        return false;

      if (colPaydateFilter) {
        const rowDate = row.paydate
          ? new Date(row.paydate).toISOString().split("T")[0]
          : "";
        if (rowDate !== colPaydateFilter) return false;
      }

      if (
        colReasonFilter &&
        !row.delete_comment
          ?.toLowerCase()
          .includes(colReasonFilter.toLowerCase())
      )
        return false;
      if (
        colRequestedByFilter &&
        !row.requested_by_name
          ?.toLowerCase()
          .includes(colRequestedByFilter.toLowerCase())
      )
        return false;

      if (
        colDeletedByFilter &&
        !row.deleted_by_name
          ?.toLowerCase()
          .includes(colDeletedByFilter.toLowerCase())
      )
        return false;

      if (colDeletedAtFilter) {
        const rowDate = row.deleted_at
          ? new Date(row.deleted_at).toISOString().split("T")[0]
          : "";
        if (rowDate !== colDeletedAtFilter) return false;
      }

      return true;
    });
  };

  render() {
    const filteredData = this.filterData();

    const pageOptions = {
      sizePerPage: 50,
      totalSize: filteredData.length,
      custom: true,
    };

    return (
      <React.Fragment>
        <div className="page-content">
          <MetaTags>
            <title>Deleted Payments | NHS NEQAS</title>
          </MetaTags>
          <Container fluid>
            <Breadcrumbs title="Labs" breadcrumbItem="Deleted Payments" />
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody>
                    <Row className="mb-3">
                      <Col md={3}>
                        <Label for="nameFilter">Lab / Participant Name</Label>
                        <Select
                          id="nameFilter"
                          options={this.getUniqueOptions(
                            "participant_name"
                          ).map(name => ({ value: name, label: name }))}
                          value={
                            this.state.nameFilter
                              ? {
                                  value: this.state.nameFilter,
                                  label: this.state.nameFilter,
                                }
                              : null
                          }
                          onChange={selected =>
                            this.setState({ nameFilter: selected?.value || "" })
                          }
                          placeholder="Select Lab / Participant"
                          isClearable
                        />
                      </Col>

                      <Col md={2}>
                        <Label>Deleted From</Label>
                        <Input
                          type="date"
                          value={this.state.dateFromFilter}
                          onChange={e =>
                            this.handleTopFilterChange(
                              "dateFromFilter",
                              e.target.value
                            )
                          }
                        />
                      </Col>
                      <Col md={2}>
                        <Label>Deleted To</Label>
                        <Input
                          type="date"
                          value={this.state.dateToFilter}
                          onChange={e =>
                            this.handleTopFilterChange(
                              "dateToFilter",
                              e.target.value
                            )
                          }
                        />
                      </Col>
                    </Row>

                    <PaginationProvider
                      pagination={paginationFactory(pageOptions)}
                      keyField="id"
                      columns={this.state.columns}
                      data={filteredData}
                    >
                      {({ paginationProps, paginationTableProps }) => (
                        <React.Fragment>
                          <div className="table-responsive">
                            <BootstrapTable
                              keyField="id"
                              columns={this.state.columns}
                              data={filteredData}
                              {...paginationTableProps}
                              defaultSorted={[
                                { dataField: "deleted_at", order: "desc" },
                              ]}
                              bordered={false}
                              striped
                              responsive
                              headerWrapperClasses={"table-header-sky-blue"}
                              noDataIndication={() =>
                                "No deleted payments found"
                              }
                            />
                          </div>
                          <Row className="mt-2">
                            <Col className="pagination pagination-rounded justify-content-end">
                              <PaginationListStandalone {...paginationProps} />
                            </Col>
                          </Row>
                        </React.Fragment>
                      )}
                    </PaginationProvider>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </React.Fragment>
    );
  }
}

DeletedPayments.propTypes = {
  DeletedPayments: PropTypes.array,
  onGetDeletedPayments: PropTypes.func,
};

const mapStateToProps = state => ({
  DeletedPayments: state.AddPayment?.DeletedPayments ?? [],
});

const mapDispatchToProps = dispatch => ({
  onGetDeletedPayments: filters => dispatch(getDeletedPayments(filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DeletedPayments);
